import {
  Delivery_Status as PBDeliveryStatus,
  DeliveryStop_DeliveryAttempt_Status as PBDeliveryAttemptStatus,
  DeliveryStop_Status as PBDeliveryStopStatus,
  Sale_Status as PBSaleStatus,
} from "gen/txn/models_pb"

import { Money } from "../classes/money"
import { UUID } from "../classes/uuid"
import { Address } from "../libs/address"
import { SaleStatusLabel, SaleStatusLabels } from "../types/sales/sales.types"
import { ensure } from "../utils"
import { DeliveryWindow } from "./deliveryWindow"

export interface BaseDelivery {
  id: UUID
  identifier: string
  status: DeliveryStatus
  departureTime: Date
  returnTime?: Date
  stops: BaseDeliveryStop[]
  // TODO (Umer): make this required by updating GetDelivery endpoint
  dispatchLocation?: {
    id: UUID
    prefix: string
    name: string
  }
  carrier?: string
  driver?: {
    id?: UUID
    name: string
  }
  notes?: string
  internalNotes?: string
}

export interface BaseDeliveryStop {
  id: UUID
  status: DeliveryStopStatus
  position: number // 0 while staged; 1-indexed when attached
  details: DeliveryStopDetails
  transactions: StopTransaction[]
  internalNotes: string
  deliveryAttempts: DeliveryAttempt[] // most recent first
}

export interface DeliveryAttempt {
  id: UUID
  status: DeliveryAttemptStatus
  at: Date
  byName: string
  signatureAttachmentId?: UUID
  photoAttachmentIds: UUID[]
  notes: string
}

export interface DeliveryContact {
  name: string
  phone: string
  email: string
}

export interface DeliveryStopDetails {
  address: Address
  contact: DeliveryContact
  customerContactIds: string[]
  notes: string
  requestedDate?: Date
  requestedWindow: DeliveryWindow
}

/**
 * A transaction associated with a delivery stop.
 */
export interface StopTransaction {
  id: UUID
  identifier: string
  numItems: number
  saleStatus: SaleStatusLabel
  total: Money
  customerName: string
  // TODO (Umer): Consolidate customerId and customerName into a single object
  customerId?: UUID
}

export const DeliveryStatuses = {
  [PBDeliveryStatus.UNDEFINED]: undefined,
  [PBDeliveryStatus.SCHEDULED]: "Scheduled",
  [PBDeliveryStatus.IN_PROGRESS]: "In Progress",
  [PBDeliveryStatus.COMPLETED]: "Completed",
  [PBDeliveryStatus.VOIDED]: "Cancelled",
} as const

export type DeliveryStatus = NonNullable<
  (typeof DeliveryStatuses)[keyof typeof DeliveryStatuses]
>

export function adaptDeliveryStatus(status?: PBDeliveryStatus): DeliveryStatus {
  return ensure(
    status === undefined ? undefined : DeliveryStatuses[status],
    `Missing delivery status: ${status}`
  )
}

export const deliveryStatusToPB: Record<DeliveryStatus, PBDeliveryStatus> = {
  Scheduled: PBDeliveryStatus.SCHEDULED,
  "In Progress": PBDeliveryStatus.IN_PROGRESS,
  Completed: PBDeliveryStatus.COMPLETED,
  Cancelled: PBDeliveryStatus.VOIDED,
}

// Sales can attach to deliveries in these statuses
export const attachableDeliveryStatuses: DeliveryStatus[] = [
  "Scheduled",
  "In Progress",
]

const DeliveryStopStatuses = {
  [PBDeliveryStopStatus.UNDEFINED]: undefined,
  [PBDeliveryStopStatus.ATTACHED]: "Attached",
  [PBDeliveryStopStatus.LOADED]: "Loaded",
  [PBDeliveryStopStatus.IN_PROGRESS]: "In Progress",
  [PBDeliveryStopStatus.DELIVERED]: "Delivered",
  [PBDeliveryStopStatus.CANCELLED]: "Cancelled",
  [PBDeliveryStopStatus.STAGED]: "Staged",
} as const

export type DeliveryStopStatus = NonNullable<
  (typeof DeliveryStopStatuses)[keyof typeof DeliveryStopStatuses]
>

export function adaptDeliveryStopStatus(
  status?: PBDeliveryStopStatus
): DeliveryStopStatus {
  return ensure(
    status === undefined ? undefined : DeliveryStopStatuses[status],
    `Missing delivery stop status: ${status}`
  )
}

const DeliveryAttemptStatuses = {
  [PBDeliveryAttemptStatus.UNDEFINED]: undefined,
  [PBDeliveryAttemptStatus.FAIL]: "Failed",
  [PBDeliveryAttemptStatus.SUCCESS]: "Delivered",
} as const

export type DeliveryAttemptStatus = NonNullable<
  (typeof DeliveryAttemptStatuses)[keyof typeof DeliveryAttemptStatuses]
>

export function adaptDeliveryAttemptStatus(
  status?: PBDeliveryAttemptStatus
): DeliveryAttemptStatus {
  return ensure(
    status === undefined ? undefined : DeliveryAttemptStatuses[status],
    `Missing delivery attempt status: ${status}`
  )
}

export function adaptSaleStatus(status?: PBSaleStatus): SaleStatusLabel {
  return ensure(
    status === undefined ? undefined : SaleStatusLabels[status],
    `Missing sale status: ${status}`
  )
}
